import { RequirementItem, RequirementItemType } from "@AccountConfig/account-settings/workbench-settings";
import { EvidenceProcessorItem, IntegrationItem, RuleItem, SectionItem } from "@AccountConfig/account-settings/workbench-settings/components/workbench-requirements-config/workbench-requirements-config.component";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, ViewEncapsulation, OnInit, AfterViewInit, Input, ViewChild, OnDestroy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatSelectChange } from "@angular/material/select";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { AppService } from "@App/app.service";
import { CreateRequirementRequest, PatchRequirementsSortOrderRequest, PatchRequirementRuleRequest, PatchRequirementEvidenceProcessorRequest, PatchRequirementIncludeInCasePackageRequest, PatchRequirementSectionRequest, PatchRequirementIntegrationRequest } from "@CaseSummary/underwriting/models";
import PatchRequirementAutomaticallySummarizeRequest from "@CaseSummary/underwriting/models/uww-patch-requirement-automatically-summarize-request";
import RequirementResponse from "@CaseSummary/underwriting/models/uww-requirement-response";
import { UWRequirementsDataService, UWWorkbenchService } from "@CaseSummary/underwriting/services";
import { CaseRequirementStatuses } from "@CaseSummary/underwriting/types";
import { AccountSettingsDTO, UwwActionDTO } from "@DTOs";
import { IntegrationEnum } from "@Enums";
import Account from "@Models/account";
import Client from "@Models/global/client";
import { ConfirmationDialogService, NotificationService, NotificationSeverity } from "@Services";
import SectionDataService from "@Services/case/section.data.service";
import RulesDataService from "@Services/config/rules.data.service";
import { AccountConfigStateService } from "../../../../../services/account-config-state.service";
import { SharedModule } from "@Shared/shared.module";
import { TimeUtils, Utils } from "@Utils";
import { forkJoin } from "rxjs";
import { SubSink } from "subsink";
import { MatPaginator } from "@angular/material/paginator";
import { ObjectTagsComponent } from "@Components/object-tags/object-tags.component";
import { MrsSearchFieldComponent } from "@Shared/components/mrs-search-field/mrs-search-field.component";
import PatchRequirementAutoCompleteRuleRequest from "@CaseSummary/underwriting/models/uww-patch-requirement-autocompleterule-request";


interface CreateRequirementConfig {
    name: string;
    sortOrder: number;
    sectionId?: string;
    autoCompleteRuleId?: string;
    ruleId?: string;
    integrationId?: string;
    integrationActionId?: string;
    autoSummarizeEnabled?: boolean;
    associationId?: string;
    includeInCasePackage?: boolean;
    associationMode?: string;
}

@Component({
    selector: 'workbenchv2-requirements-config',
    templateUrl: './workbenchv2-requirements-config.component.html',
    styleUrls: ['workbenchv2-requirements-config.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [SharedModule, ObjectTagsComponent, MrsSearchFieldComponent]
})

export default class WorkbenchV2RequirementsConfigComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() client: Client;
    @Input() account: Account;

@ViewChild('table', {static: true}) table: MatTable<RequirementItem>;
    @ViewChild('paginator') paginator: MatPaginator;



    dataLoaded = false; // All necessary data has been loaded
    dataSource = new MatTableDataSource<any>();

    //Association
    evidenceProcessors: EvidenceProcessorItem[] = [{id: IntegrationEnum.DigitalOwl, name: 'Digital Owl '}];    
    noEvidenceProcessor: EvidenceProcessorItem = { id: Utils.emptyGuid, name: 'None'};
    selectedEvidenceProcessorId: string = this.noEvidenceProcessor.id;

    // Requirements
    lastUpdated: Date;
    requirementsLoaded = false;
    requirements: RequirementItem[] = [];
    requirementNamesLowerCase: string[] = [];
    addingRequirement = false;

    // Sections & Integrations = RequirementType 
    sectionsLoaded = false;
    noSection: SectionItem = { id: '00000000-0000-0000-0000-000000000000', name: 'None', isDisabled: false };
    sections: SectionItem[] = [];
    
    //Integrations
    integrationPostActions: IntegrationItem[] = [];
    integrationPostActionsLoaded = false;

    // Rules
    rulesLoaded = false;
    noRule: RuleItem = { id: '00000000-0000-0000-0000-000000000000', name: 'None', isDisabled: false };
    rules: RuleItem[] = [];

    regularSummary = '00000000-0000-0000-0000-000000000001';
    caseNotesSummary = '00000000-0000-0000-0000-000000000002';

    columnsToDisplay: string[] = ['requirement'];
    
    filterValue: string;

    subs = new SubSink();
    protected get RequirementItemType() { return RequirementItemType; } // Make enums available in template


    notOrderedStatus = CaseRequirementStatuses.NotOrdered;
    orderedStatus = CaseRequirementStatuses.Ordered;
    initialStatus: string;
    accountSettings: AccountSettingsDTO[] = [];
    
    public requirementForm: UntypedFormGroup;
    selectedRequirement: RequirementItem | null = null;
    originalRequirement: RequirementItem | null = null; // Backup for cancel functionality
    manual = 'Manual'; // Used for disabled select option
    isAutoSummarizeDisabled = false;

    constructor(
        private readonly appService: AppService,
        private readonly uwRequirementsDataService: UWRequirementsDataService,
        private readonly uwWorkbenchService: UWWorkbenchService,
        private readonly sectionDataService: SectionDataService,
        private readonly rulesDataService: RulesDataService,
        private readonly notificationService: NotificationService,
        private readonly stateService: AccountConfigStateService,        
        private readonly _fb: UntypedFormBuilder,
        private readonly confirmationService: ConfirmationDialogService
    ) {
    }

    //#region Helpers


    updateData_section(requirement: RequirementItem): RequirementItem {
        if (requirement.sectionId == null) return requirement;
        if (requirement.sectionId == this.noSection.id) return requirement;

        const section = this.sections.find(s => s.id === requirement.sectionId);
        if (section) {
            section.isDisabled = true;
        }
    }

    updateData_integrationPostAction(requirement: RequirementItem): RequirementItem {
        if (requirement.integrationActionId == null) return requirement;
        if (requirement.integrationActionId == Utils.emptyGuid) return requirement;

        const integrationPostAction = this.integrationPostActions.find(x => x.integrationId === requirement.integrationId && x.actionid == requirement.integrationActionId);
        if (integrationPostAction) {
            integrationPostAction.isDisabled = true;
        }
    }

    updateDataLoaded() {
        if (!this.requirementsLoaded) return;
        if (!this.sectionsLoaded) return;
        if (!this.rulesLoaded) return;
        if (!this.integrationPostActionsLoaded) return;

        // Enable all Sections because we won't know which Requirements had sections removed, only which section currently have sections.
        this.sections.forEach(section => { section.isDisabled = false; });

        this.integrationPostActions.forEach(x => { x.isDisabled = false; });

        // Disable the Sections that are currently on Requirements
        this.requirements.forEach(requirement => {
            this.updateData_section(requirement);
            this.updateData_integrationPostAction(requirement);
        });

        const sortedRequirements = [...this.requirements].sort((a, b) => b.updatedOn.getTime() - a.updatedOn.getTime());
        this.lastUpdated = sortedRequirements[0]?.updatedOn;
        this.dataLoaded = true;
    }

    mapRequirementResponseToItem(existingRequirements: RequirementResponse[]) {
        const items = existingRequirements.map(x => {
            const requirementTypeId = x.sectionId ?? x.integrationActionId;

            const requirementIntegrationMatch = this.evidenceProcessors.find(y => y.id == x.evidenceProcessor?.processorId);
            const requirementItem: RequirementItem = {
                ...x,
                id: x.id,
                name: x.name,
                sortOrder: x.sortOrder,
                type: RequirementItemType.Existing,
                errorMessage: '',
                updatedOn: TimeUtils.convertToDateCst(x.audit?.updatedOn ?? x.audit?.createdOn),
                sectionId: x.sectionId ?? this.noSection.id,
                ruleId: x.ruleId ?? this.noRule.id,
                autoCompleteRuleId: x.autoCompleteRuleId ?? this.noRule.id,
                integrationId: x.integrationId ?? Utils.emptyGuid,
                formattedIntegrationName: requirementIntegrationMatch ? 'Integration: ' + requirementIntegrationMatch.name : this.noEvidenceProcessor.name,
                selectedEvidenceProcessorId: x.evidenceProcessor?.processorId ?? this.noEvidenceProcessor.id,
                selectedRequirementTypeId: requirementTypeId ?? Utils.emptyGuid,
                initialStatus: x.initialStatus ?? this.notOrderedStatus
            };
            return requirementItem;
        });

        return items;
    }

    createNewRequirement(config: CreateRequirementConfig): RequirementItem {
        let evidenceProcesser = null;
        
        if ( config.associationId !== undefined && config.associationId !== this.noEvidenceProcessor.id) {
            evidenceProcesser = {
                processorType: 'summarize',
                mode: config.associationMode || this.regularSummary,
                processorId: config.associationId && config.associationId !== null && config.associationId !== Utils.emptyGuid ? config.associationId : this.noEvidenceProcessor.id
            };
        }

        const requirementItem: RequirementItem = {
            id: Utils.emptyGuid,
            name: config.name,
            sortOrder: config.sortOrder,
            type: RequirementItemType.Added,
            errorMessage: '',
            sectionId: config.sectionId,
            ruleId: config.ruleId,
            autoCompleteRuleId: config.autoCompleteRuleId,
            integrationId: config.integrationId,
            integrationActionId: config.integrationActionId,
            autoSummarizeEnabled: config.autoSummarizeEnabled,
            includeInCasePackage: config.includeInCasePackage,
            evidenceProcessor: evidenceProcesser,
            initialStatus: this.initialStatus,
            selectedEvidenceProcessorId: evidenceProcesser?.processorId ?? this.noEvidenceProcessor.id
        };
        return requirementItem;
    }

    updateSortOrder() {
        this.requirements.forEach((requirement, index) => requirement.sortOrder = index);
    }

    nameAlreadyExists(name: string) {
        return this.requirementNamesLowerCase.includes(name.toLowerCase());
    }

    // Clamp position input value to valid range
    clampPositionValue(event: any) {
        const input = event.target;
        const value = +input.value;
        const maxValue = this.requirements?.length || 1;
        input.value = Math.max(1, Math.min(value, maxValue));
    }

    //#endregion
    //#region Subscriptions and loaders
    subscribeRules() {
        this.rulesDataService.load(this.account.id).subscribe(rules => {
            const ruleItems: RuleItem[] = rules.map((rule): RuleItem => ({ id: rule.id, name: rule.name, isDisabled: false }));
            ruleItems.unshift(this.noRule); // add no rule default to front of array

            this.rules = ruleItems;
            this.rulesLoaded = true;
            this.updateDataLoaded();
        });
    }

    subscribeToRequirements() {
        this.subs.add(this.uwRequirementsDataService.loadRequirements(this.client.code, this.account.id).subscribe(requirementResponses => {
            this.requirements = this.mapRequirementResponseToItem(requirementResponses);

            this.setInitialStatus();

            this.requirementNamesLowerCase = this.requirements.map(x => x.name.toLowerCase());
            this.requirementsLoaded = true;
            this.refreshDataSource();
            this.updateDataLoaded();
        }));
    }

    setInitialStatus(){
        const uniqueStatus = [...new Set(this.requirements.map(item => item.initialStatus))];

        this.initialStatus = uniqueStatus[0];  // don't think this is right. if the first item is an integration requirement it won't have the status that a user selected for manual or workflow requirements...
    }

    subscribeToSections() {
        this.subs.add(this.sectionDataService.loadSectionsForWorkflow(this.account.workflowId).subscribe(sections => {
            const sectionSelectItems = sections.map(section => ({
                id: section.id,
                name: section.name,
                isDisabled: false
            }));
            
            this.sections = [...sectionSelectItems];

            this.sectionsLoaded = true;
            this.updateDataLoaded();
        }));
    }


    subscribeToAccountActions() {
        forkJoin({
            globalActions: this.appService.getData(`Actions/client/${this.client.id}`),
            accountActions: this.appService.getData(`Actions/account/${this.account.id}`),
            accountSettings: this.appService.getData(`AccountSettings/${this.account.id}`),
        }).subscribe({
            next: (action) => {
                const globalActions = action.globalActions.data;
                const accountActions: UwwActionDTO[] = action.accountActions.data;
                const suppressedIntegrationsAccountSetting = action.accountSettings.data?.find(x => x.name == "uwwCaseRequirementExcludedIntegrations");

                accountActions?.forEach(accountAction => {
                    const globalAction = globalActions.find(globalAction => globalAction.id == accountAction.globalActionId);
                    if (globalAction.integration_Id || accountAction.v1IntegrationId) { // make sure we have an integration id
                        const globalActionName = globalAction?.name;
                        const actionName = (accountAction.actionName) ?? globalActionName; 

                        const integrationId = globalAction.integration_Id ?? accountAction.v1IntegrationId;
                        if ((!suppressedIntegrationsAccountSetting) || !suppressedIntegrationsAccountSetting.value.includes(integrationId)) // finally, filter based on account setting
                            this.integrationPostActions.push({integrationId: integrationId, name: actionName, actionid: accountAction.actionId, isDisabled: false });
                    }
                });

                this.integrationPostActions.sort((a, b) => this.sortIntegrationPostActions(a, b));

                this.integrationPostActionsLoaded = true;

                this.updateDataLoaded();
            }
        });
    }    

    sortIntegrationPostActions(a: IntegrationItem, b: IntegrationItem) {
        if (a.name != b.name)
            return a.name.localeCompare(b.name);

        return a.actionid.localeCompare(b.actionid);
    }
    //#endregion
    //#region Lifecycle

    ngOnInit() {
        this.uwWorkbenchService.setClientInfo(this.client);

        this.subscribeRules();
        this.subscribeToRequirements();
        this.subscribeToSections();
        this.subscribeToAccountActions();

        this.requirementForm = this._fb.group({
            id: [''],
            name: ['', [Validators.required]],
            requirementType: [''],
            ruleId: [''],
            autoCompleteRuleId: [''],
            sectionId: [''],
            integrationActionId: [''],
            includeInCasePackage: [false], 
            autoSummarizeEnabled: [false],
            evidenceProcessor: [''] ,
            evidenceMode: ['']           
        });

        // Watch for selectedRequirement changes to populate form
        if (this.selectedRequirement) {
            this.populateEditForm(this.selectedRequirement);
        }
    }
    
    ngAfterViewInit() {
        // Connect paginator to dataSource after view is initialized
        this.refreshDataSource();
    }
    
    private refreshDataSource() {
        if (this.paginator) {
            this.dataSource.paginator = this.paginator;
        }
        this.dataSource.data = this.requirements;
        
        this.dataSource.filterPredicate = this.tableFilter().bind(this);
    }


    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    //#endregion
    
    //#region Form Control Getters
    
    get name() { return this.requirementForm.get('name'); }
    get requirementType() { return this.requirementForm.get('requirementType'); }
    get ruleId() { return this.requirementForm.get('ruleId'); }
    get autoCompleteRuleId() { return this.requirementForm.get('autoCompleteRuleId'); }
    get sectionId() { return this.requirementForm.get('sectionId'); }
    get integrationActionId() { return this.requirementForm.get('integrationActionId'); }
    get includeInCasePackage() { return this.requirementForm.get('includeInCasePackage'); }
    get autoSummarizeEnabled() { return this.requirementForm.get('autoSummarizeEnabled'); }
    get evidenceMode() { return this.requirementForm.get('evidenceMode'); }
    //#endregion
    
    //#region Form Methods
    
    populateEditForm(requirement: RequirementItem) {
        this.requirementForm.patchValue({
            id: requirement.id,
            name: requirement.name,
            ruleId: requirement.ruleId,
            autoCompleteRuleId: requirement.autoCompleteRuleId,
            includeInCasePackage: requirement.includeInCasePackage,
            autoSummarizeEnabled: requirement.autoSummarizeEnabled,
            evidenceProcessor: requirement.evidenceProcessor?.processorId || this.noEvidenceProcessor.id,
            evidenceMode: requirement.evidenceProcessor?.mode
        });

        // Determine requirement type and associated value
        if (requirement.selectedRequirementTypeId === this.noSection.id) {
            this.requirementForm.patchValue({
                requirementType: 'Manual',
                sectionId: '',
                integrationActionId: ''
            });
        } else {
            // Check if it's a section
            const section = this.sections.find(s => s.id === requirement.selectedRequirementTypeId);
            if (section) {
                this.requirementForm.patchValue({
                    requirementType: 'Workflow Section',
                    sectionId: requirement.selectedRequirementTypeId,
                    integrationActionId: ''
                });
            } else {
                // It's a post action
                const postAction = this.integrationPostActions.find(pa => pa.actionid === requirement.selectedRequirementTypeId);
                if (postAction) {
                    this.requirementForm.patchValue({
                        requirementType: 'Integration',
                        sectionId: '',
                        integrationActionId: requirement.selectedRequirementTypeId
                    });
                }
            }
        }

        setTimeout(() => {
            this.onEvidenceProcessorChange();
        }, 0);
    }
    
    isProcessorNone(): boolean {
        return this.requirementForm?.get('evidenceProcessor')?.value === this.noEvidenceProcessor?.id;
    }

    onEvidenceProcessorChange(): void {
        const processorValue = this.requirementForm.get('evidenceProcessor')?.value;
        this.isAutoSummarizeDisabled = processorValue === this.noEvidenceProcessor?.id;

        if (!this.isAutoSummarizeDisabled && (!this.requirementForm.get('evidenceMode')?.value 
            ||  this.requirementForm.get('evidenceMode')?.value === '' 
            || this.requirementForm.get('evidenceMode')?.value === null)) {
            this.requirementForm.patchValue({ evidenceMode: this.regularSummary });
            this.requirementForm.get('evidenceMode').updateValueAndValidity();
        }

        if (this.isAutoSummarizeDisabled) {
            this.requirementForm.patchValue({ autoSummarizeEnabled: false, evidenceMode: null });
        }

        this.requirementForm.updateValueAndValidity();
    }

    handleCancelEditRequirement() {
        // If it's a temporary requirement (starts with 'temp-'), remove it from the array
        if (this.selectedRequirement?.id?.startsWith('temp-')) {
            const index = this.requirements.findIndex(r => r.id === this.selectedRequirement.id);
            if (index > -1) {
                this.requirements.splice(index, 1);
                this.refreshDataSource();
            }
        } else if (this.originalRequirement && this.selectedRequirement) {
            // Restore the original requirement to cancel any changes made
            const index = this.requirements.findIndex(r => r.id === this.selectedRequirement.id);
            if (index > -1) {
                this.requirements[index] = this.originalRequirement;
                this.refreshDataSource();
            }
        }
        
        this.selectedRequirement = null;
        this.originalRequirement = null;
        this.requirementForm.reset();
    }
    
    updateRequirementFromForm() : RequirementItem{
        const selectedPostAction = this.integrationPostActions.find(x => x.actionid == this.requirementForm.value.integrationActionId);
        const updatedRequirement ={
            ...this.selectedRequirement,
            name: this.requirementForm.value.name,
            ruleId: this.requirementForm.value.ruleId !== '' ? this.requirementForm.value.ruleId : null,
            autoCompleteRuleId: this.requirementForm.value.autoCompleteRuleId !== '' ? this.requirementForm.value.autoCompleteRuleId : null,
            sectionId: this.requirementForm.value.sectionId !== '' && this.requirementForm.value.sectionId !== null ? this.requirementForm.value.sectionId : null,
            integrationActionId: this.requirementForm.value.integrationActionId !== '' && this.requirementForm.value.integrationActionId !== null ? this.requirementForm.value.integrationActionId : null,
            integrationId: (this.requirementForm.value.integrationActionId === Utils.emptyGuid ) ? null : selectedPostAction?.integrationId,
            autoSummarizeEnabled: this.requirementForm.value.autoSummarizeEnabled,
            includeInCasePackage: this.requirementForm.value.includeInCasePackage,
        };

        if (this.requirementForm.value.evidenceProcessor === Utils.emptyGuid) {
            updatedRequirement.evidenceProcessor = null;
        } else {
            updatedRequirement.evidenceProcessor = {
                processorType: 'summarize',                
                processorId: this.requirementForm.value.evidenceProcessor,
                mode: this.requirementForm.value.evidenceMode
            };
        }

        updatedRequirement.attachmentTags = this.selectedRequirement?.attachmentTags || [];

        return updatedRequirement;
    }

    onRequirementTypeChange(): void {
    // Clear sectionId and integrationActionId when requirement type changes
    this.requirementForm.patchValue({
        sectionId: null,
        integrationActionId: null
    });
    }

    handleSaveEditedRequirement() {
        if (this.requirementForm.valid && this.selectedRequirement) {
            const formValue = this.requirementForm.value;
            
            // If it's a temporary requirement, create a new one
            if (this.selectedRequirement.id?.startsWith('temp-')) {
                this.createRequirementFromForm(formValue);
            } else {
                const updatedRequirement = this.updateRequirementFromForm();
                this.uwWorkbenchService.patchRequirement(updatedRequirement).subscribe((response) =>{
                    this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
                });

                this.selectedRequirement = null;
                this.originalRequirement = null;
                this.requirementForm.reset();
            }
        }
    }
    
    private createRequirementFromForm(formValue: any) {
        const name = formValue.name;
        
        // Validate name
        if (!name || name.trim() === '') {
            this.requirementForm.get('name')?.setErrors({ required: true });
            return;
        }
        
        // Check if name already exists (excluding current requirement)
        if (this.nameAlreadyExists(name) && 
            !this.requirements.some(r => r.id === this.selectedRequirement.id && r.name.toLowerCase() === name.toLowerCase())) {
            this.requirementForm.get('name')?.setErrors({ duplicate: name });
            return;
        }

        const rule = formValue.ruleId;
        const autoCompleteRule = formValue.autoCompleteRuleId;
        
        let sectionId = null;
        let integrationId = null;
        let integrationActionId = null;
        let typeId = null;
        
        // Determine type based on form selection
        if (formValue.requirementType === 'Workflow Section' && formValue.sectionId) {
            typeId = formValue.sectionId;
            const matchingSection = this.sections.find(x => x.id == typeId);
            if (matchingSection) {
                sectionId = matchingSection.id;
            }
        } else if (formValue.requirementType === 'Integration' && formValue.integrationActionId) {
            typeId = formValue.integrationActionId;
            const matchingIntegrationPostAction = this.integrationPostActions.find(x => x.actionid == typeId);
            if (matchingIntegrationPostAction) {
                integrationId = matchingIntegrationPostAction.integrationId;
                integrationActionId = matchingIntegrationPostAction.actionid;
            }
        }
        
        // Has the section already been selected, Bug-32238
        if (sectionId) {
            const selectedSection = this.sections.find(x => x.id === sectionId);
            if (selectedSection?.isDisabled) {
                this.requirementForm.get('sectionId')?.setErrors({ duplicateSection: selectedSection.name });
                return;
            }
        }
        
        // Get evidence processor from form or default
        const associationId = formValue.evidenceProcessor || this.noEvidenceProcessor.id;

        const autoSummarizeEnabled = formValue.autoSummarizeEnabled || false;
        const includeInCasePackage = formValue.includeInCasePackage || false;
        
        // Remove the temporary requirement from the array
        const tempIndex = this.requirements.indexOf(this.selectedRequirement);
        if (tempIndex > -1) {
            this.requirements.splice(tempIndex, 1);
        }
        
        this.addingRequirement = true;
        const sortOrder = -1; // Add at top
        
        // Use the same createNewRequirement method as handleAddRequirement
        const newRequirementItem = this.createNewRequirement({
            name,
            sortOrder,
            sectionId,
            ruleId: rule,
            autoCompleteRuleId: autoCompleteRule,
            integrationId,
            integrationActionId,
            autoSummarizeEnabled,
            associationId,
            includeInCasePackage,
            associationMode: this.requirementForm.value.evidenceMode
        });

        newRequirementItem.attachmentTags = this.selectedRequirement?.attachmentTags || [];

        // Create the request object following the same pattern as handleAddRequirement
        const createRequirement: CreateRequirementRequest = { ...newRequirementItem, accountId: this.account.id };
        
        this.uwWorkbenchService.createRequirement(createRequirement).subscribe({
            next: (_response) => {
                this.addingRequirement = false;
                this.selectedRequirement = null;
                this.originalRequirement = null;
                this.requirementForm.reset();
                // Reload requirements to get the new one with proper ID
                this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
                this.notificationService.showNotification({
                    severity: NotificationSeverity.Success,
                    message: "Requirement added"
                });
            },
            error: (error) => {
                this.addingRequirement = false;
                // Re-add the temporary requirement on error
                this.requirements.splice(tempIndex, 0, this.selectedRequirement);
                this.refreshDataSource();
            }
        });
    }
    
    //#endregion
    //#region Handlers

    handleRequirementSelection(requirement: RequirementItem) {
        if (this.selectedRequirement !== null) {
            this.notificationService.showNotification({
                severity: NotificationSeverity.Warning,
                message: "Please save or cancel the current requirement before selecting another."
            });
            return;
        }

        this.selectedRequirement = requirement;
        // Create a deep copy backup for cancel functionality
        this.originalRequirement = structuredClone(requirement);
        this.populateEditForm(requirement);
    }

    handleAddRequirementClick() {
        if (this.selectedRequirement !== null) {
            this.notificationService.showNotification({
                severity: NotificationSeverity.Warning,
                message: "Please save or cancel the current requirement before adding a new one."
            });
            return;
        }

        // Create a new empty requirement with a temporary ID
        const tempId = 'temp-' + Date.now();
        const newRequirement: RequirementItem = {
            id: tempId,
            name: '',
            sortOrder: -1, // Place at top
            type: RequirementItemType.Added,
            errorMessage: '',
            sectionId: this.noSection.id,
            ruleId: this.noRule.id,
            autoCompleteRuleId: this.noRule.id,
            integrationId: Utils.emptyGuid,
            integrationActionId: undefined,
            autoSummarizeEnabled: false,
            includeInCasePackage: false,
            evidenceProcessor: {
                processorType: 'summarize',
                processorId: this.noEvidenceProcessor.id
            },
            initialStatus: this.initialStatus || this.notOrderedStatus,
            selectedEvidenceProcessorId: this.noEvidenceProcessor.id,
            selectedRequirementTypeId: this.noSection.id,
            formattedIntegrationName: this.noEvidenceProcessor.name
        };

        // Add to the beginning of the requirements array
        this.requirements.unshift(newRequirement);
        
        // Set as selected requirement for editing
        this.selectedRequirement = newRequirement;
        
        // Populate the form with empty/default values
        this.populateEditForm(newRequirement);
        
        // Refresh the data source to show the new requirement
        this.paginator?.firstPage();
        this.refreshDataSource();
    }

    handleDeleteRequirement(requirementToDelete: RequirementItem) {
        this.confirmationService.open({
            message: 'Are you sure that you want to delete this requirement?',
            onOk: () => {
                this.uwWorkbenchService.deleteRequirement(requirementToDelete.id).subscribe(_response => {
                    this.selectedRequirement = null;
                    this.originalRequirement = null;
                    this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
                });
            },
            showCancel: true
        });
        
    }

    handleSortChange(dropEvent: CdkDragDrop<string>) {
        const previousIndex = this.requirements.findIndex(d => d === dropEvent.item.data);

        // Calculate the actual global target index considering pagination
        const pageOffset = this.paginator.pageIndex * this.paginator.pageSize;
        const globalTargetIndex = pageOffset + dropEvent.currentIndex;
        
        // Ensure the global index is within bounds
        const clampedTargetIndex = Math.max(0, Math.min(globalTargetIndex, this.requirements.length - 1));

        moveItemInArray(this.requirements, previousIndex, clampedTargetIndex);
        
        // Update the data source to reflect the changes
        this.dataSource.data = [...this.requirements];
        
        // DEV NOTE: The d&d tool re-ordered the `this.requirements` array.
        // Now we need to make sure we update their sortOrder property to match their array index.
        this.updateSortOrder();

        const patchSortOrderRequest: PatchRequirementsSortOrderRequest = {
            accountId: this.account.id,
            requirementsOrder: this.requirements.map(r => ({ requirementId: r.id, sortOrder: r.sortOrder }))
        };

        this.uwWorkbenchService.patchSortOrder(patchSortOrderRequest).subscribe(_response => {
            this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
        });
    }

    handleMoveUp(requirement: RequirementItem) {
        const currentIndex = this.requirements.findIndex(r => r.id === requirement.id);
        if (currentIndex > 0) {
            // Swap with the item above
            const temp = this.requirements[currentIndex - 1];
            this.requirements[currentIndex - 1] = this.requirements[currentIndex];
            this.requirements[currentIndex] = temp;
            
            this.updateSortOrderAndSave();
        }
    }

    handleMoveDown(requirement: RequirementItem) {
        const currentIndex = this.requirements.findIndex(r => r.id === requirement.id);
        if (currentIndex < this.requirements.length - 1) {
            // Swap with the item below
            const temp = this.requirements[currentIndex + 1];
            this.requirements[currentIndex + 1] = this.requirements[currentIndex];
            this.requirements[currentIndex] = temp;
            
            this.updateSortOrderAndSave();
        }
    }

    handleMoveToPosition(requirement: RequirementItem, newPosition: number) {
        const currentIndex = this.requirements.findIndex(r => r.id === requirement.id);
        const targetIndex = Math.max(0, Math.min(newPosition - 1, this.requirements.length - 1)); // Convert to 0-based index and clamp
        
        if (currentIndex !== targetIndex && targetIndex >= 0) {
            // Remove the item from its current position
            const item = this.requirements.splice(currentIndex, 1)[0];
            // Insert it at the new position
            this.requirements.splice(targetIndex, 0, item);
            
            this.updateSortOrderAndSave();
        }
    }

    private updateSortOrderAndSave() {
        // Update sort orders to match array indices
        this.updateSortOrder();
        
        // Refresh the data source
        this.refreshDataSource();

        const patchSortOrderRequest: PatchRequirementsSortOrderRequest = {
            accountId: this.account.id,
            requirementsOrder: this.requirements.map(r => ({ requirementId: r.id, sortOrder: r.sortOrder }))
        };

        this.uwWorkbenchService.patchSortOrder(patchSortOrderRequest).subscribe(_response => {
            this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
        });
    }

    handleSectionChange(change: MatSelectChange, requirementId: string) {
        const selectedSectionId = change.value;

        this.patchRequirementSection(requirementId, selectedSectionId);
    }

    handleRuleChange(change: MatSelectChange, requirementId: string) {
        const selectedRuleId = change.value;

        const request: PatchRequirementRuleRequest = {
            accountId: this.account.id,
            requirementId: requirementId,
            ruleId: (selectedRuleId === this.noRule.id) ? null : selectedRuleId
        };

        this.uwWorkbenchService.patchRequirementRule(request).subscribe(_ => {
            this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
        });
    }

    handleAutoCompleteRuleChange(change: MatSelectChange, requirementId: string) {
        const selectedRuleId = change.value;

        const request: PatchRequirementAutoCompleteRuleRequest = {
            accountId: this.account.id,
            requirementId: requirementId,
            ruleId: (selectedRuleId === this.noRule.id) ? null : selectedRuleId
        };

        this.uwWorkbenchService.patchRequirementAutoCompleteRule(request).subscribe(_ => {
            this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
        });
    }

    handleSummarizeChange(change: MatSelectChange, requirementId: string) {
        const selectedSummaryId = change.value;

        const request: PatchRequirementEvidenceProcessorRequest = {
            accountId: this.account.id,
            requirementId: requirementId,
            evidenceProcessor: (selectedSummaryId === this.noEvidenceProcessor.id) ? null : {
                processorType: 'summarize',
                processorId: selectedSummaryId
            }
        };

        this.uwWorkbenchService.patchRequirementEvidenceProcessor(request).subscribe(_ => {
            this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
        });
        
    }

    handleIncludeInCasePackage(change: MatSlideToggleChange, requirementId: string) {
        const request: PatchRequirementIncludeInCasePackageRequest = {
            requirementId: requirementId,
            includeInCasePackage: change.checked,
        };
        this.uwWorkbenchService.patchRequirementIncludeInCasePackage(request).subscribe(_ => {
            this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
        });
    }

    handleAutomaticallySummarizeToggle(change: MatSlideToggleChange, requirementId: string) {
        const request: PatchRequirementAutomaticallySummarizeRequest = {
            requirementId: requirementId,
            autoSummarizeEnabled: change.checked,
        };
        this.uwWorkbenchService.patchRequirementAutoSummarizedEnabled(request).subscribe(_ => {
            this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
        });
    }

    async handleTypeChange(change: MatSelectChange, requirementId: string) {
        const selectedTypeId = change.value;

        if (this.sections.some(section => section.id === selectedTypeId)) {
            // we have a section
            await this.patchRequirementSection(requirementId, selectedTypeId).then(
                async _ => await this.patchRequirementIntegration(requirementId, Utils.emptyGuid)
            );
        } else if (this.integrationPostActions.some(x => x.actionid === selectedTypeId)) {
            // we have an integration
            await this.patchRequirementIntegration(requirementId, selectedTypeId).then(
                async _ => await this.patchRequirementSection(requirementId, Utils.emptyGuid)
            );
        } else {
            await this.patchRequirementSection(requirementId, Utils.emptyGuid).then(
                async _ => await this.patchRequirementIntegration(requirementId, Utils.emptyGuid)
            );
        }
    }


    handleDefaultStatusChange(change: MatSelectChange) {
        const selectedDefaultId = change.value;

        this.uwWorkbenchService.patchInitialStatus(this.account.id, selectedDefaultId).subscribe(() => {            
            this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
        });
    }

    async patchRequirementSection(requirementId: string, selectedSectionId: string, deleteIntegration = false) {
        const request: PatchRequirementSectionRequest = {
            accountId: this.account.id,
            requirementId: requirementId,
            sectionId: (selectedSectionId === this.noSection.id) ? null : selectedSectionId
        };

        await this.uwWorkbenchService.patchRequirementSection(request).then(_ => {
            this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
        });
    }

    async patchRequirementIntegration(requirementId: string, selectedPostActionId: string) {
        const selectedPostAction = this.integrationPostActions.find(x => x.actionid == selectedPostActionId);
        const request: PatchRequirementIntegrationRequest = {
            accountId: this.account.id,
            requirementId: requirementId,
            integrationId: (selectedPostActionId === Utils.emptyGuid || selectedPostAction.integrationId === Utils.emptyGuid) ? null : selectedPostAction.integrationId,
            actionId: (selectedPostActionId === Utils.emptyGuid) ? null : selectedPostActionId
        };

        await this.uwWorkbenchService.patchRequirementIntegration(request).then(_ => {
            this.uwRequirementsDataService.reloadRequirements(this.client.code, this.account.id);
        });
    }

    searchRequirements() {
        this.dataSource.filter = 'filter';
    }

    tableFilter(): (data: RequirementItem, filter: string) => boolean {
        return function (data, filter): boolean {
            
            if (this.filterValue && this.filterValue.trim() !== '') {

                let requirementType: string;
                if (data.selectedRequirementTypeId === this.noSection.id) {
                    requirementType = 'Manual';
                } else if (this.sections.some(s => s.id === data.selectedRequirementTypeId)) {
                    requirementType = 'Workflow Section';
                } else {
                    requirementType = 'Integration';
                }

                const integrationName = this.integrationPostActions.find(x => x.actionid === data.selectedRequirementTypeId)?.name ?? '';
                const sectionName = this.sections.find(s => s.id === data.selectedRequirementTypeId)?.name ?? '';
                const ruleName = this.rules.find(r => r.id === data.ruleId)?.name ?? '';
                const autoCompleteRuleName = this.rules.find(r => r.id === data.autoCompleteRuleId)?.name ?? ''; 
                const result = 
                    (requirementType && requirementType.toLowerCase().includes(this.filterValue.toLowerCase()))
                    || (data.name && data.name?.toLowerCase().includes(this.filterValue.toLowerCase()))         
                    || (integrationName && integrationName?.toLowerCase().includes(this.filterValue.toLowerCase()))
                    || (sectionName && sectionName?.toLowerCase().includes(this.filterValue.toLowerCase()))
                    || (ruleName && ruleName?.toLowerCase().includes(this.filterValue.toLowerCase()))
                    || autoCompleteRuleName?.toLowerCase().includes(this.filterValue.toLowerCase())
                    || (data.attachmentTags?.some((tag: string) => tag.toLowerCase().includes(this.filterValue.toLowerCase())));       
                
                return result;
            }

            return true;
        };
    }
    //#endregion
}